/**
 * AI confirm card — shown after Quick Capture AI's `process` returns
 * suggestions that need user confirmation (any field change or new
 * client question). Notes that were auto-appended already landed in
 * Airtable; this card only gates the destructive writes.
 *
 * The user can:
 *   * Toggle each field-update on/off (per-row checkbox).
 *   * Edit each suggested value inline.
 *   * Toggle each new question on/off + edit inline.
 *   * Apply selected → calls /apply with the curated subset.
 *   * Discard → calls /discard, no Airtable writes.
 *
 * On success/discard, the parent re-fetches the task and clears the card
 * via `onResolved`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Check, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  aiQuickCaptureApi,
  type AiQuickCaptureProcessResponse,
  type AiSuggestedFieldUpdates,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatAiError, formatAiWarnings } from '@/lib/ai-errors';

interface AiConfirmCardProps {
  taskId: string;
  proposal: AiQuickCaptureProcessResponse;
  onResolved: () => void;
}

interface FieldRowState {
  key: keyof AiSuggestedFieldUpdates;
  label: string;
  enabled: boolean;
  value: string;
}

const FIELD_LABELS: Record<keyof AiSuggestedFieldUpdates, string> = {
  status: 'Status',
  priority: 'Priority',
  nextAction: 'Next Action',
  definitionOfDone: 'Definition of Done',
};

export function AiConfirmCard({ taskId, proposal, onResolved }: AiConfirmCardProps) {
  // Initialize per-field rows. Each suggestion is OFF by default — user must
  // explicitly opt in. Mirrors the Pass 3 hard rule: no destructive writes
  // without confirmation.
  const initialFields = useMemo<FieldRowState[]>(() => {
    return (Object.keys(FIELD_LABELS) as Array<keyof AiSuggestedFieldUpdates>)
      .map((key) => {
        const raw = proposal.suggested.fieldUpdates[key];
        const value = typeof raw === 'string' ? raw.trim() : '';
        if (!value) return null;
        return { key, label: FIELD_LABELS[key], enabled: false, value };
      })
      .filter((row): row is FieldRowState => row !== null);
  }, [proposal.suggested.fieldUpdates]);

  const initialQuestions = useMemo(
    () =>
      (proposal.suggested.newQuestions ?? [])
        .map((q) => q.trim())
        .filter((q) => q.length > 0)
        .map((q, idx) => ({ id: `q-${idx}`, enabled: false, text: q })),
    [proposal.suggested.newQuestions],
  );

  const [fields, setFields] = useState<FieldRowState[]>(initialFields);
  const [questions, setQuestions] = useState(initialQuestions);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Sync state if a new proposal arrives without unmount.
  useEffect(() => setFields(initialFields), [initialFields]);
  useEffect(() => setQuestions(initialQuestions), [initialQuestions]);

  const applyMutation = useMutation({
    mutationFn: () => {
      const fieldUpdates: AiSuggestedFieldUpdates = {};
      fields
        .filter((row) => row.enabled && row.value.trim().length > 0)
        .forEach((row) => {
          fieldUpdates[row.key] = row.value.trim();
        });
      const newQuestions = questions
        .filter((q) => q.enabled && q.text.trim().length > 0)
        .map((q) => q.text.trim());
      return aiQuickCaptureApi.apply(taskId, {
        logId: proposal.logId,
        fieldUpdates,
        newQuestions,
      });
    },
    onSuccess: () => onResolved(),
    onError: (err) => setErrorMsg(formatError(err)),
  });

  const discardMutation = useMutation({
    mutationFn: () => {
      if (!proposal.logId) return Promise.resolve({ ok: true } as const);
      return aiQuickCaptureApi.discard(taskId, proposal.logId);
    },
    onSuccess: () => onResolved(),
    onError: (err) => setErrorMsg(formatError(err)),
  });

  const hasFields = fields.length > 0;
  const hasQuestions = questions.length > 0;
  const anySelected =
    fields.some((row) => row.enabled) || questions.some((q) => q.enabled);
  const busy = applyMutation.isPending || discardMutation.isPending;

  return (
    <Card className="space-y-3 border-primary/30 bg-primary/[0.04] p-3">
      <div className="flex items-center gap-2 text-[12px] font-semibold text-primary">
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        AI suggestions — review before applying
      </div>

      {proposal.notesAppended && (
        <div className="rounded-md border border-border bg-card px-2.5 py-2 text-[12px] text-muted-foreground">
          <span className="font-medium text-foreground">Notes appended.</span>{' '}
          A new entry was added to Task Notes — existing content was preserved.
        </div>
      )}

      {formatAiWarnings(proposal.warnings, proposal.logId).length > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-[12px]">
          {formatAiWarnings(proposal.warnings, proposal.logId).map((w) => (
            <div key={w} className="flex items-start gap-1.5">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-warning" aria-hidden />
              <span className="text-muted-foreground">{w}</span>
            </div>
          ))}
        </div>
      )}

      {hasFields && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Field changes
          </div>
          {fields.map((row, idx) => (
            <FieldRow
              key={row.key}
              row={row}
              onToggle={(enabled) =>
                setFields((prev) => prev.map((r, i) => (i === idx ? { ...r, enabled } : r)))
              }
              onValueChange={(value) =>
                setFields((prev) => prev.map((r, i) => (i === idx ? { ...r, value } : r)))
              }
            />
          ))}
        </div>
      )}

      {hasQuestions && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            New questions for client
          </div>
          {questions.map((q, idx) => (
            <QuestionRow
              key={q.id}
              question={q}
              onToggle={(enabled) =>
                setQuestions((prev) => prev.map((r, i) => (i === idx ? { ...r, enabled } : r)))
              }
              onTextChange={(text) =>
                setQuestions((prev) => prev.map((r, i) => (i === idx ? { ...r, text } : r)))
              }
            />
          ))}
        </div>
      )}

      {!hasFields && !hasQuestions && (
        <div className="text-[12px] text-muted-foreground">
          Nothing else to apply — Notes were already updated.
        </div>
      )}

      {proposal.reasoning && (
        <div className="text-[11px] italic text-muted-foreground">
          AI: {proposal.reasoning}
        </div>
      )}

      {errorMsg && (
        <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-[12px] text-destructive">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-1.5 pt-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => discardMutation.mutate()}
          disabled={busy}
          className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          Discard
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => applyMutation.mutate()}
          disabled={busy || !anySelected}
          className="h-8 gap-1.5"
        >
          {applyMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden />
          )}
          {hasFields || hasQuestions ? 'Apply Selected' : 'Done'}
        </Button>
      </div>
    </Card>
  );
}

function FieldRow({
  row,
  onToggle,
  onValueChange,
}: {
  row: FieldRowState;
  onToggle: (enabled: boolean) => void;
  onValueChange: (value: string) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-2 rounded-md border border-border bg-card px-2.5 py-2 text-[12px] transition-colors',
        row.enabled && 'border-primary/40 bg-primary/[0.06]',
      )}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-3.5 w-3.5"
        checked={row.enabled}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {row.label}
        </div>
        <input
          type="text"
          value={row.value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={!row.enabled}
          className="mt-0.5 w-full bg-transparent text-foreground focus:outline-none disabled:text-muted-foreground"
        />
      </div>
    </label>
  );
}

function QuestionRow({
  question,
  onToggle,
  onTextChange,
}: {
  question: { id: string; enabled: boolean; text: string };
  onToggle: (enabled: boolean) => void;
  onTextChange: (text: string) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-2 rounded-md border border-border bg-card px-2.5 py-2 text-[12px] transition-colors',
        question.enabled && 'border-primary/40 bg-primary/[0.06]',
      )}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-3.5 w-3.5"
        checked={question.enabled}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <textarea
        value={question.text}
        onChange={(e) => onTextChange(e.target.value)}
        disabled={!question.enabled}
        rows={1}
        className="min-w-0 flex-1 resize-none bg-transparent text-foreground focus:outline-none disabled:text-muted-foreground"
      />
    </label>
  );
}

function formatError(err: unknown): string {
  return formatAiError(err, 'Apply failed');
}
